// Repeating events (2026-10-13). A repeating event is ONE events/ doc with
//   repeat: { freq: "day" | "week" | "month" | "year", days: [0–6] (Sun = 0; weekly only), until: "YYYY-MM-DD" | null }
//   repeatSkip: ["YYYY-MM-DD", …]  (dates taken out of the series: "just this one" edits and deletes)
// Calendar.js expands it into one occurrence per date at load time; nothing per-occurrence is stored.
// Schedules (Dashboard) are repeating Busy events with schedule: true (+ paused, futurePods, syncGoogle).
import { localDateKey } from "./dateUtils";

export const addDaysKey = (key, n) => {
  const d = new Date(key + "T00:00");
  d.setDate(d.getDate() + n);
  return localDateKey(d);
};
const daysBetween = (a, b) => Math.round((new Date(b + "T00:00") - new Date(a + "T00:00")) / 86400000);

export const weekdayOf = (key) => new Date(key + "T00:00").getDay();

// Normalizes what the form produces: weekly with no days picked repeats on the start date's weekday.
export function cleanRepeat(repeat, startDate) {
  if (!repeat) return null;
  const days = repeat.freq === "week" ? [...new Set(repeat.days?.length ? repeat.days : [weekdayOf(startDate)])].sort() : [];
  return { freq: repeat.freq, days, until: repeat.until || null };
}

// Dates (start keys) the series occurs on whose span touches [fromKey, toKey].
export function occurrenceDates(ev, fromKey, toKey) {
  const r = ev.repeat;
  const start = ev.startDate;
  const span = ev.endDate && ev.endDate > start ? daysBetween(start, ev.endDate) : 0;
  const last = r.until && r.until < toKey ? r.until : toKey;
  const skip = new Set(ev.repeatSkip || []);
  const out = [];
  const keep = (k) => k >= start && k <= last && addDaysKey(k, span) >= fromKey && !skip.has(k);
  if (r.freq === "day" || r.freq === "week") {
    const days = r.freq === "week" ? (r.days?.length ? r.days : [weekdayOf(start)]) : null;
    let k = fromKey > start ? addDaysKey(fromKey, -span) : start;
    if (k < start) k = start;
    for (; k <= last; k = addDaysKey(k, 1)) {
      if (days && !days.includes(weekdayOf(k))) continue;
      if (keep(k)) out.push(k);
    }
  } else {
    const s = new Date(start + "T00:00");
    for (let n = 0; ; n++) {
      const d = r.freq === "month" ? new Date(s.getFullYear(), s.getMonth() + n, s.getDate()) : new Date(s.getFullYear() + n, s.getMonth(), s.getDate());
      if (d.getDate() !== s.getDate()) continue; // no Feb 30 / Feb 29 in other years: skip that month or year
      const k = localDateKey(d);
      if (k > last) break;
      if (keep(k)) out.push(k);
    }
  }
  return out;
}

// One entry per occurrence for repeating events (id "<docId>~<date>", _seriesId, _occ); others pass through.
// Paused schedules show nothing.
export function expandRepeats(list, fromKey, toKey) {
  const out = [];
  for (const ev of list) {
    if (!ev.repeat) { out.push(ev); continue; }
    if (ev.paused) continue;
    const span = ev.endDate && ev.endDate > ev.startDate ? daysBetween(ev.startDate, ev.endDate) : 0;
    for (const k of occurrenceDates(ev, fromKey, toKey)) {
      out.push({ ...ev, id: `${ev.id}~${k}`, _seriesId: ev.id, _occ: k, startDate: k, endDate: addDaysKey(k, span) });
    }
  }
  return out;
}

const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// "Every Thursday", "Weekdays", "Every Mon, Wed", "Every month on the 15th", "Every year on Oct 15" (+ " until Dec 17")
export function repeatLabel(repeat, startDate) {
  if (!repeat) return "";
  const d = new Date(startDate + "T00:00");
  let s;
  if (repeat.freq === "day") s = "Every day";
  else if (repeat.freq === "week") {
    const days = repeat.days?.length ? [...repeat.days].sort() : [d.getDay()];
    const key = days.join(",");
    s = key === "1,2,3,4,5" ? "Weekdays" : key === "0,6" ? "Weekends" : key === "0,1,2,3,4,5,6" ? "Every day"
      : days.length === 1 ? `Every ${DAY_LONG[days[0]]}` : `Every ${[...days.filter((x) => x), ...days.filter((x) => !x)].map((x) => DAY_SHORT[x]).join(", ")}`;
  } else if (repeat.freq === "month") {
    const n = d.getDate();
    const sfx = n % 10 === 1 && n !== 11 ? "st" : n % 10 === 2 && n !== 12 ? "nd" : n % 10 === 3 && n !== 13 ? "rd" : "th";
    s = `Every month on the ${n}${sfx}`;
  } else s = `Every year on ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  if (repeat.until) s += ` until ${new Date(repeat.until + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
  return s;
}

// Google Calendar recurrence lines for a series (RRULE + EXDATE for skipped dates).
export function toGoogleRecurrence(ev, timeZone) {
  const r = ev.repeat;
  const BY = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
  const compact = (k) => k.replace(/-/g, "");
  let rule = `RRULE:FREQ=${{ day: "DAILY", week: "WEEKLY", month: "MONTHLY", year: "YEARLY" }[r.freq]}`;
  if (r.freq === "week") rule += `;BYDAY=${(r.days?.length ? r.days : [weekdayOf(ev.startDate)]).map((x) => BY[x]).join(",")}`;
  // Timed events need UNTIL as a UTC instant: the end of the last day, local time (an instant, so toISOString is right here)
  if (r.until) rule += ev.startTime ? `;UNTIL=${new Date(r.until + "T23:59:59").toISOString().replace(/[-:]|\.\d+/g, "")}` : `;UNTIL=${compact(r.until)}`;
  const lines = [rule];
  const skips = (ev.repeatSkip || []).slice().sort();
  if (skips.length) {
    lines.push(ev.startTime
      ? `EXDATE;TZID=${timeZone}:${skips.map((k) => `${compact(k)}T${ev.startTime.replace(":", "")}00`).join(",")}`
      : `EXDATE;VALUE=DATE:${skips.map(compact).join(",")}`);
  }
  return lines;
}
